import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Youtube } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const FiereEventiLanci = () => {
  const { d } = useLang();
  const p = d.fierePage;
  return (
    <div data-testid="fiere-eventi-page">
      <section className="relative overflow-hidden bg-ink py-20 text-paper lg:py-28 hero-grain" data-testid="fiere-hero">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent-bright"
            >
              <span className="h-px w-10 bg-accent" /> {p.heroLabel}
            </motion.p>
            <h1 className="max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="fiere-headline">
              <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
              <MaskedLine delay={0.3}><span className="italic text-accent-bright">{p.heroL2}</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg"
            >
              {p.sub}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }} className="mt-10">
              <Link
                to="/contatti?area=fiera"
                data-testid="fiere-hero-cta"
                className="inline-flex items-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
              >
                {p.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative overflow-hidden rounded-md">
              <img src="/images/monaco-1.webp" alt={p.heroImgAlt} className="aspect-[4/3] w-full object-cover" data-testid="fiere-hero-image" />
              <span className="absolute bottom-4 left-4 rounded-sm bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-accent-bright backdrop-blur-sm" data-testid="fiere-hero-badge">
                {p.heroImgBadge}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="fiere-metodo">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.metodo.overline} title={p.metodo.title} lead={p.metodo.lead} />
          <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-4 lg:grid-cols-7" data-testid="fiere-steps">
            {p.metodo.steps.map((s, i) => (
              <Reveal key={s} delay={i * 0.06} className="group bg-paper p-6 transition-colors duration-300 hover:bg-ink">
                <p className="font-serif text-4xl text-outline transition-colors duration-300 group-hover:text-accent group-hover:[-webkit-text-stroke:0]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-ink transition-colors duration-300 group-hover:text-paper">
                  {s}
                </h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="genova-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHead overline={p.genova.overline} title={p.genova.title} lead={p.genova.body} />
            </div>
            <div className="flex flex-col justify-end gap-8 sm:flex-row lg:col-span-5 lg:flex-col lg:items-end">
              {p.genova.stats.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.1} className="sm:text-right" data-testid={`genova-stat-${i}`}>
                  <p className="font-serif text-6xl tracking-tight text-accent lg:text-7xl">{s.value}</p>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{s.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15} className="mt-4">
            <p className="text-xs text-mutedink">{p.genova.statsNote}</p>
          </Reveal>
          <Reveal delay={0.2} className="mt-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{p.genova.galleryLabel}</p>
            <div className="-mx-6 mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12" data-testid="genova-gallery">
              {[
                { src: "/images/genova-stand.webp", alt: "Lo stand MED Defense al Salone Nautico di Genova 2024" },
                { src: "/images/genova-banner.webp", alt: "Branding MED Defense sulle scale del Salone Nautico" },
                { src: "/images/genova-defense.webp", alt: "I gommoni MED Defense in acqua al Salone" },
                { src: "/images/genova-team.webp", alt: "Il team MED RHIB allo stand" },
                { src: "/images/genova-boat.webp", alt: "Uscita in mare con il team durante il Salone" },
              ].map((g, i) => (
                <div key={g.src} className="group w-72 shrink-0 snap-center overflow-hidden rounded-md" data-testid={`genova-gallery-${i}`}>
                  <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25} className="mt-14">
            <div className="hero-grain relative rounded-md bg-ink p-8 text-paper sm:p-12" data-testid="genova-social">
              <div className="relative grid min-w-0 grid-cols-1 gap-10 lg:grid-cols-12">
                <div className="min-w-0 lg:col-span-5">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{p.genova.social.label}</p>
                  <h3 className="mt-4 font-serif text-3xl leading-tight sm:text-4xl">{p.genova.social.title}</h3>
                  <p className="mt-5 text-sm leading-relaxed text-white/65">{p.genova.social.body}</p>
                  <div className="mt-8 flex flex-wrap gap-x-8 gap-y-6 sm:gap-10">
                    <div data-testid="genova-social-stat-a">
                      <p className="font-serif text-4xl text-accent-bright sm:text-5xl">{p.genova.social.statA}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{p.genova.social.statALabel}</p>
                    </div>
                    <div data-testid="genova-social-stat-b">
                      <p className="font-serif text-4xl text-accent-bright sm:text-5xl">{p.genova.social.statB}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{p.genova.social.statBLabel}</p>
                    </div>
                  </div>
                  {p.genova.social.videos && (
                    <div className="mt-8" data-testid="genova-videos">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{p.genova.social.videosLabel}</p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {p.genova.social.videos.map((url, vi) => (
                          <a
                            key={url}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`genova-video-${vi}`}
                            className="inline-flex items-center gap-2 rounded-md border border-white/25 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-white/80 transition-colors duration-300 hover:border-accent-bright hover:text-accent-bright"
                          >
                            <Youtube className="h-4 w-4" /> Video {vi + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex min-w-0 items-start justify-center gap-4 lg:col-span-7">
                  {[
                    { src: "/images/genova-social-1.webp", alt: "Profilo Instagram @gruppomed durante il Salone Nautico di Genova 2024" },
                    { src: "/images/genova-social-2.webp", alt: "Reel Instagram del Salone con decine di migliaia di visualizzazioni" },
                    { src: "/images/genova-social-3.webp", alt: "Reel Instagram del Salone — contenuti e visualizzazioni" },
                  ].map((g, i) => (
                    <div key={g.src} className={`w-1/3 overflow-hidden rounded-md border border-white/15 ${i === 1 ? "mt-8" : ""}`} data-testid={`genova-social-img-${i}`}>
                      <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[9/19] w-full object-cover object-top" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="fiere-more-fairs">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.moreFairs.overline} title={p.moreFairs.title} />
          <div className="mt-14 grid grid-cols-1 gap-8">
            {[...p.moreFairs.cards].sort((a, b) => (a.gallery ? 0 : 1) - (b.gallery ? 0 : 1)).map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08} className="min-w-0 rounded-md border border-ink/12 bg-paper" data-testid={`more-fair-${i}`}>
                <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
                  <div className="min-w-0 p-7 lg:col-span-4 lg:p-10">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{f.brand} — {f.place}</p>
                    <h3 className="mt-2 font-serif text-3xl text-ink">{f.title}</h3>
                    {f.badge && (
                      <span className="mt-4 inline-block rounded-sm bg-accent px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        {f.badge}
                      </span>
                    )}
                    <p className="mt-4 text-sm leading-relaxed text-mutedink">{f.body}</p>
                    {f.stats && (
                      <div className="mt-6 grid grid-cols-2 gap-4 border-t border-ink/10 pt-5 sm:grid-cols-4" data-testid={`more-fair-stats-${i}`}>
                        {f.stats.map((s) => (
                          <div key={s.label}>
                            <p className="font-serif text-2xl text-accent">{s.value}</p>
                            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] leading-snug text-mutedink">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {f.video && (
                      <a
                        href={f.video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`more-fair-video-${i}`}
                        className="mt-5 inline-flex items-center gap-2 self-start rounded-md border border-ink/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-accent hover:text-accent"
                      >
                        <Youtube className="h-4 w-4 text-accent" /> {f.video.label}
                      </a>
                    )}
                    {f.quote && (
                      <figure className="mt-6 rounded-md border border-ink/12 bg-white p-5" data-testid={`more-fair-quote-${i}`}>
                        <blockquote className="font-serif text-base italic leading-relaxed text-ink">{f.quote.text}</blockquote>
                        <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{f.quote.author}</figcaption>
                      </figure>
                    )}
                  </div>
                  {f.gallery ? (
                    <div className="min-w-0 pb-7 lg:col-span-8 lg:py-10 lg:pr-10">
                      <p className="px-7 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink lg:px-0">{f.galleryLabel}</p>
                      <div className="mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-7 pb-2 lg:px-0" data-testid={`more-fair-gallery-${i}`}>
                        {f.gallery.map((src, gi) => (
                          <div key={src} className="group w-64 shrink-0 snap-center overflow-hidden rounded-md sm:w-80" data-testid={`more-fair-gallery-${i}-${gi}`}>
                            <img src={src} alt={`${f.title} — ${f.brand}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : f.img ? (
                    <div className="min-w-0 pb-7 lg:col-span-8 lg:py-10 lg:pr-10">
                      <div className="flex px-7 pb-2 lg:px-0">
                        <div className="w-64 shrink-0 overflow-hidden rounded-md border border-ink/10 bg-white sm:w-80">
                          <img src={f.img} alt={`${f.title} — ${f.brand}`} loading="lazy" className="aspect-[4/3] w-full object-contain" data-testid={`more-fair-img-${i}`} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative m-7 mt-0 flex min-h-64 items-end overflow-hidden rounded-md bg-ink p-8 hero-grain lg:col-span-8 lg:m-10 lg:ml-0">
                      <span className="relative font-serif text-4xl italic text-paper/90 lg:text-6xl">{f.brand}</span>
                      <span className="absolute right-6 top-6 font-mono text-[10px] uppercase tracking-[0.25em] text-accent-bright">{f.place}</span>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="fiere-packages">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.packages.overline} title={p.packages.title} lead={p.packages.lead} />
          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            {p.packages.list.map((pkg, i) => (
              <Reveal
                key={pkg.name}
                delay={i * 0.1}
                className="flex flex-col rounded-md border border-ink/12 bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent lg:p-10"
                data-testid={`package-${slug(pkg.name)}`}
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{pkg.name}</p>
                <div className="mt-5 border-y border-ink/10 py-5">
                  <p className="whitespace-nowrap font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                    {pkg.price} <span className="font-sans text-sm font-normal text-mutedink">{pkg.vat}</span>
                  </p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{pkg.duration}</p>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-mutedink">{pkg.problem}</p>
                {pkg.includes && (
                  <p className="mt-5 text-xs font-semibold uppercase tracking-[0.12em] text-ink" data-testid={`package-${slug(pkg.name)}-includes`}>{pkg.includes}</p>
                )}
                <ul className={`flex-1 space-y-3 text-sm ${pkg.includes ? "mt-4" : "mt-6 border-t border-ink/10 pt-6"}`}>
                  {pkg.features.map((f) => (
                    <li key={f} className="flex gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                      <span className="text-ink/85">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contatti?area=fiera"
                  data-testid={`package-${slug(pkg.name)}-cta`}
                  className="mt-8 inline-flex items-center justify-center gap-3 rounded-md bg-accent px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
                >
                  {p.packages.cta} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={`/pacchetti/${slug(pkg.name)}`}
                  data-testid={`package-${slug(pkg.name)}-detail`}
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-md border border-ink/20 px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-accent hover:text-accent"
                >
                  {d.pkg.common.detailCta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-14">
            <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-mutedink">{p.packages.costsTitle}</p>
            <div className="grid gap-px overflow-hidden rounded-md bg-ink/10 md:grid-cols-2" data-testid="fiere-costs">
              <div className="bg-paper p-8 lg:p-10" data-testid="fiere-costs-fee">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{p.packages.costsFee.t}</p>
                <p className="mt-3 text-sm leading-relaxed text-ink/85">{p.packages.costsFee.b}</p>
              </div>
              <div className="bg-ink p-8 text-paper lg:p-10" data-testid="fiere-costs-excluded">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{p.packages.costsExcluded.t}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/75">{p.packages.costsExcluded.b}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection interest="fiera" />
    </div>
  );
};

export default FiereEventiLanci;
